<script setup lang="ts">
// The status contract of one environment (FR-36..FR-40), its actions, access and the live log (UJ-1, UJ-2), in tabs;
// the tab is in the URL (?tab=), so a link can point at the checks or the log.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ApiProblem, must } from '../api/client';
import type { Environment } from '../api/types';
import { useContext } from '../context';
import {
  buildLabel, classificationBadge, dataChecksBanner, conditionBadge, conditionSummary, displayUrl, formatTime, healthTiles, imageBasis,
  orderedConditions, phaseBadge, relativeTime, roleTone, shopLinks, shortSha, verificationText,
} from '../status/status';
import StatusBadge from '../components/StatusBadge.vue';
import ProblemAlert from '../components/ProblemAlert.vue';
import ActionPanel from '../components/ActionPanel.vue';
import AccessPanel from '../components/AccessPanel.vue';
import ClassificationPanel from '../components/ClassificationPanel.vue';
import DataChecksPanel from '../components/DataChecksPanel.vue';
import LiveLog from '../components/LiveLog.vue';
import AppLogsPanel from '../components/AppLogsPanel.vue';
import DbConsolePanel from '../components/DbConsolePanel.vue';
import Icon from '../components/Icon.vue';

const props = defineProps<{ name: string }>();
const { api } = useContext();
const route = useRoute();
const router = useRouter();
const env = ref<Environment>();
const error = ref<unknown>();
const gone = ref(false);
const loadedAt = ref<string>();
let timer: number | undefined;

async function load() {
  try {
    env.value = must(await api.GET('/environments/{name}', { params: { path: { name: props.name } } }));
    error.value = undefined;
    loadedAt.value = new Date().toISOString();
  } catch (e) {
    if (e instanceof ApiProblem && e.code === 'EnvironmentNotFound' && env.value) gone.value = true;
    error.value = e;
  }
  schedule();
}

function schedule() {
  window.clearTimeout(timer);
  const e = env.value;
  const busy = !!e && (!!e.action?.inFlight || !!e.deleting || e.observedGeneration < e.generation || e.conditions.some((c) => c.type === 'Reconciling' && c.status === 'True'));
  if (!gone.value) timer = window.setTimeout(load, busy ? 3000 : 10_000);
}

onMounted(load);
onBeforeUnmount(() => window.clearTimeout(timer));
watch(() => props.name, () => { env.value = undefined; gone.value = false; void load(); });

const TABS = ['overview', 'checks', 'data', 'application', 'activity', 'logs', 'database', 'access'] as const;
type Tab = (typeof TABS)[number];
const tab = computed<Tab>(() => (TABS.includes(route.query.tab as Tab) ? (route.query.tab as Tab) : 'overview'));
const tabLink = (t: Tab) => ({ name: 'detail', params: { name: props.name }, query: t === 'overview' ? {} : { tab: t } });
const go = (t: Tab) => router.push(tabLink(t));

const phase = computed(() => env.value && phaseBadge(env.value.phase));
const summary = computed(() => env.value && conditionSummary(env.value));
const conditions = computed(() => orderedConditions(env.value?.conditions ?? []));
const tiles = computed(() => healthTiles(env.value?.conditions ?? []));
const tilesOk = computed(() => tiles.value.filter((t) => t.state === 'ok' || t.state === 'warn').length);
const checksBanner = computed(() => dataChecksBanner(env.value));
const reconciling = computed(() => env.value?.conditions.find((c) => c.type === 'Reconciling'));
const classification = computed(() => classificationBadge(env.value?.dataClassification));
const LINK_LABEL: Record<string, string> = { shop: 'Peaksolution', cashdesk: 'Cashdesk', ticketservice: 'Ticket service', contingentservice: 'Contingent service' };
const links = computed(() => (env.value ? shopLinks(env.value) : []));
// Operator self-service: mint a signed URL and open the Peaksolution backend logged in as yourself. Needs the
// operator role AND the data entitlement (dataAccess), exactly what the endpoint enforces, so the button is not
// offered when it could only 403.
const canShopLogin = computed(
  () => !!env.value?.yourRole && env.value.yourRole !== 'viewer' && !!env.value.dataAccess,
);
// Database writes: the same rule as shop login (operator role and the data entitlement), as the endpoint enforces.
const canWriteDb = computed(() => canShopLogin.value);
const shopBusy = ref(false);
async function shopLogin() {
  shopBusy.value = true;
  // Open the tab synchronously, inside the click gesture: the POST round-trip would otherwise consume the
  // user activation and the popup would be blocked. The URL is filled once it arrives (opener cleared for
  // noopener semantics); if the popup was blocked, navigate in place instead.
  const win = window.open('about:blank', '_blank');
  try {
    const r = must(await api.POST('/environments/{name}/shop-login', { params: { path: { name: props.name } } }));
    error.value = undefined;
    if (win) {
      win.opener = null;
      win.location.href = r.url;
    } else {
      window.location.href = r.url;
    }
  } catch (e) {
    error.value = e;
    if (win) win.close();
  } finally {
    shopBusy.value = false;
  }
}
const app = computed(() => env.value?.builds?.peaksolution);
/** cashdesk, ticketservice and contingentservice: the build asked for (spec) and the one on disk (status.builds). */
const services = computed(() =>
  (['cashdesk', 'ticketservice', 'contingentservice'] as const).map((key) => ({
    key,
    label: LINK_LABEL[key]!,
    requested: env.value?.spec[key]?.enabled === false ? undefined : env.value?.spec[key]?.build,
    status: env.value?.builds?.[key],
  })),
);
const history = computed(() => env.value?.action.history ?? []);
const failedCount = computed(() => history.value.filter((h) => h.outcome !== 'Succeeded').length);
const onlyFailed = ref(false);
const shownHistory = computed(() => (onlyFailed.value ? history.value.filter((h) => h.outcome !== 'Succeeded') : history.value));
const sourceSwitch = computed(() => {
  const e = env.value;
  const d = e?.db?.source;
  return e && d && (d.customer !== e.source.customer || d.environment !== e.source.environment) ? `${d.customer} / ${d.environment}` : undefined;
});
const people = computed(() => (env.value?.access.owner ? 1 : 0) + (env.value?.access.members.length ?? 0));
const active = computed(() => {
  const e = env.value;
  return !!e && !e.tornDown && (!!e.action?.inFlight || e.observedGeneration < e.generation || e.conditions.some((c) => c.type === 'Reconciling' && c.status === 'True'));
});
const seenLag = computed(() => env.value && env.value.observedGeneration < env.value.generation);
const tileIcon = (s: string) => (s === 'ok' || s === 'warn' ? 'check' : s === 'bad' ? 'cross' : 'pending');
const tileColor = (s: string) => (s === 'ok' ? 'var(--ok-dot)' : s === 'warn' ? '#9a6400' : s === 'bad' ? 'var(--bad-fg)' : 'var(--accent)');
</script>

<template>
  <nav aria-label="Breadcrumb" class="crumbs"><RouterLink to="/">Environments</RouterLink> <span aria-hidden="true">/</span> {{ name }}</nav>
  <ProblemAlert v-if="!env || gone" :error="error" :context="gone ? `${name} is gone` : `Could not load ${name}`" />
  <p v-if="gone" class="info">The environment no longer exists (deleted, or you are no longer on its access list or entitled to its data).</p>

  <template v-if="env">
    <section class="page-head" style="align-items: flex-start" aria-label="Environment">
      <div class="grow">
        <div class="inline" style="gap: 12px">
          <h1>{{ env.name }}</h1>
          <StatusBadge v-if="phase" :badge="phase" data-testid="phase" />
          <StatusBadge v-if="summary && summary.tone !== 'ok'" class="summary" :badge="summary" />
        </div>
        <div class="meta-row" style="margin-top: 8px">
          <span>Data source <span class="mono" style="color: var(--fg)">{{ env.source.customer }} / {{ env.source.environment }}</span>
            <span v-if="classification" class="pill sm" :class="`tone-${classification.tone}`" :title="classification.title" style="margin-left: 4px">{{ classification.label }}<template v-if="env.classificationOverride?.holds"> · override</template></span></span>
          <span v-if="sourceSwitch" class="pill sm tone-warn" title="A refresh-data switches the data source">switching from {{ sourceSwitch }}</span>
          <span>Installation <span v-if="env.installationId" class="mono" style="color: var(--fg)">{{ env.installationId }}</span><RouterLink v-else :to="tabLink('access')">set…</RouterLink></span>
          <span v-if="env.yourRole">You <span class="pill sm" :class="roleTone(env.yourRole)">{{ env.yourRole }}</span></span>
          <span :title="formatTime(env.createdAt)">created {{ relativeTime(env.createdAt) }} by {{ env.createdBy ?? '—' }}</span>
          <span v-if="loadedAt" class="inline" style="gap: 6px"><span class="dot" style="color: var(--ok-dot)"></span> live</span>
        </div>
      </div>
      <ActionPanel :env="env" zone="toolbar" form-to="#change-build-slot" @changed="load" />
    </section>
    <div id="change-build-slot" class="form-slot"></div>

    <nav v-if="links.length || canShopLogin" aria-label="Links" class="actions-row">
      <button v-if="canShopLogin" type="button" class="button sm" :disabled="shopBusy" data-testid="shop-login" @click="shopLogin">
        <Icon name="external" :size="14" /> {{ shopBusy ? 'Opening backend…' : 'Log in to backend' }}
      </button>
      <a v-for="l in links" :key="l.href" :href="l.href" target="_blank" rel="noopener" class="button sm" data-testid="shop-link">
        <span class="muted">{{ LINK_LABEL[l.label] ?? l.label }}</span> {{ l.href.replace(/^https?:\/\//, '').replace(/\/$/, '') }} <Icon name="external" :size="14" />
      </a>
    </nav>

    <ProblemAlert :error="error" context="Refresh failed, showing the last answer" />
    <p v-if="seenLag" class="notice" role="status">The operator has not seen the latest edit yet: it reconciled generation {{ env.observedGeneration }}, the spec is at {{ env.generation }}.</p>
    <p v-if="env.stalled?.status === 'True'" class="alert" role="alert"><strong>Stalled: {{ env.stalled.reason }}.</strong> {{ env.stalled.message }}</p>

    <nav class="tabs" aria-label="Environment sections">
      <RouterLink :to="tabLink('overview')" :aria-current="tab === 'overview' ? 'page' : undefined" data-testid="tab-overview">Overview</RouterLink>
      <RouterLink :to="tabLink('checks')" :aria-current="tab === 'checks' ? 'page' : undefined" data-testid="tab-checks">Checks <span class="count" :class="tilesOk === tiles.length ? 'tone-ok' : ''">{{ tilesOk }}/{{ tiles.length }}</span></RouterLink>
      <RouterLink :to="tabLink('data')" :aria-current="tab === 'data' ? 'page' : undefined" data-testid="tab-data">Data</RouterLink>
      <RouterLink :to="tabLink('application')" :aria-current="tab === 'application' ? 'page' : undefined" data-testid="tab-application">Application</RouterLink>
      <RouterLink :to="tabLink('activity')" :aria-current="tab === 'activity' ? 'page' : undefined" data-testid="tab-activity">Activity &amp; log <span v-if="history.length" class="count">{{ history.length }}</span></RouterLink>
      <RouterLink :to="tabLink('logs')" :aria-current="tab === 'logs' ? 'page' : undefined" data-testid="tab-logs">Logs</RouterLink>
      <RouterLink :to="tabLink('database')" :aria-current="tab === 'database' ? 'page' : undefined" data-testid="tab-database">Database</RouterLink>
      <RouterLink :to="tabLink('access')" :aria-current="tab === 'access' ? 'page' : undefined" data-testid="tab-access">Access <span class="count">{{ people }}</span></RouterLink>
    </nav>

    <!-- ================= overview -->
    <template v-if="tab === 'overview'">
      <div v-if="checksBanner" :class="checksBanner.tone === 'bad' ? 'alert banner' : 'notice banner'" :role="checksBanner.tone === 'bad' ? 'alert' : 'status'" data-testid="data-checks-banner">
        <Icon name="alert" :size="20" />
        <div class="grow"><strong>{{ checksBanner.title }}</strong> {{ checksBanner.text }}</div>
        <button type="button" class="sm" @click="go('checks')">Review</button>
      </div>

      <div class="cols">
        <div class="main-col">
          <section class="card" aria-labelledby="health-h">
            <div class="card-head">
              <h2 id="health-h">Health</h2>
              <span class="muted" style="font-size: 13px">{{ env.stalled?.status === 'True' ? 'Stalled' : 'Not stalled' }} · {{ reconciling?.status === 'True' ? `reconciling: ${reconciling.reason}` : `idle since ${relativeTime(reconciling?.lastTransitionTime)}` }}</span>
              <RouterLink class="end" :to="tabLink('checks')">All checks with messages</RouterLink>
            </div>
            <div class="tiles" data-testid="health">
              <div v-for="t in tiles" :key="t.type" class="tile" :class="{ warn: t.state === 'warn', bad: t.state === 'bad' }" :title="t.message">
                <span :style="`color: ${tileColor(t.state)}`"><Icon :name="tileIcon(t.state)" :size="20" /></span>
                <div style="min-width: 0"><div class="t-title">{{ t.title }}</div><div class="t-sub">{{ t.sub }}</div></div>
              </div>
              <p v-if="!tiles.length" class="muted">No conditions reported yet.</p>
            </div>
          </section>

          <ActionPanel :env="env" zone="danger" @changed="load" />
        </div>

        <aside class="side-col">
          <section class="card" aria-labelledby="access-sum-h">
            <div class="card-head"><h2 id="access-sum-h">Access</h2><RouterLink class="end" :to="tabLink('access')">Manage</RouterLink></div>
            <div v-if="env.access.owner" class="person">
              <span class="avatar lg" aria-hidden="true">{{ env.access.owner.username.slice(0, 2).toUpperCase() }}</span>
              <div class="who"><div style="font-weight: 600">{{ env.access.owner.username }}</div></div>
              <span class="pill sm tone-dark">owner</span>
            </div>
            <p v-else class="muted">No owner: an admin assigns one.</p>
            <p class="muted" style="font-size: 13px">
              <template v-if="env.access.members.length">Shared with {{ env.access.members.length }} {{ env.access.members.length === 1 ? 'person' : 'people' }}.</template>
              <template v-else>Not shared with anyone.</template>
              <template v-if="env.dataClassification === 'production-raw'"> The data is raw production, so people need the entitlement to <span class="mono">{{ env.source.customer }}/{{ env.source.environment }}</span> too.</template>
            </p>
            <RouterLink class="button sm" :to="tabLink('access')" style="margin-top: 8px"><Icon name="share" /> Share</RouterLink>
          </section>

          <section class="card" aria-labelledby="glance-h">
            <h2 id="glance-h">At a glance</h2>
            <dl class="facts">
              <dt>Flavour</dt><dd class="mono">{{ env.db?.flavour ?? env.flavour ?? '—' }}</dd>
              <dt>Backup</dt><dd class="mono">{{ env.db?.backupId ?? '—' }}</dd>
              <dt>MariaDB</dt><dd>{{ env.db?.mariadbVersion ?? '—' }}</dd>
              <dt>Peaksolution</dt><dd><span class="mono">{{ buildLabel(app?.base ?? env.spec.peaksolution.build.base) }}</span><span v-if="app?.base" class="sub" style="display: block">{{ verificationText(app.base) }}</span></dd>
              <template v-for="svc in services" :key="svc.key">
                <dt>{{ svc.label }}</dt><dd><span v-if="svc.requested" class="mono">{{ svc.status?.version ?? buildLabel(svc.status ?? svc.requested) }}</span><span v-else class="muted">not enabled</span></dd>
              </template>
              <dt>mysql image</dt><dd><span class="mono">{{ env.images?.mysql?.image ?? '—' }}</span><span class="sub" style="display: block">{{ imageBasis(env.images?.mysql) }}</span></dd>
              <dt>php image</dt><dd><span class="mono">{{ env.images?.php?.image ?? '—' }}</span><span class="sub" style="display: block">{{ imageBasis(env.images?.php) }}</span></dd>
              <dt>Chart</dt><dd class="mono">{{ env.chartVersion ?? '—' }}</dd>
              <dt>Operator</dt><dd class="mono">{{ env.operatorVersion ?? '—' }}</dd>
            </dl>
          </section>

          <section class="card" aria-labelledby="recent-h">
            <div class="card-head"><h2 id="recent-h">Recent activity</h2><RouterLink class="end" :to="tabLink('activity')">View all</RouterLink></div>
            <ol style="list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px">
              <li v-for="h in history.slice(0, 4)" :key="h.id" style="display: flex; gap: 10px">
                <span class="dot" :style="`color: ${h.outcome === 'Succeeded' ? 'var(--ok-dot)' : 'var(--bad-fg)'}; margin-top: 7px`"></span>
                <div style="min-width: 0"><div><strong>{{ h.type }}</strong> {{ h.outcome === 'Succeeded' ? 'succeeded' : h.outcome.toLowerCase() }}</div><div class="sub" :title="formatTime(h.finishedAt)">{{ relativeTime(h.finishedAt) }} · {{ h.reason }}</div></div>
              </li>
              <li v-if="!history.length" class="muted">No action has finished yet.</li>
            </ol>
          </section>
        </aside>
      </div>
    </template>

    <!-- ================= checks -->
    <div v-else-if="tab === 'checks'" style="display: flex; flex-direction: column; gap: 1.25rem">
    <DataChecksPanel :env="env" @changed="load" />
    <section class="card" aria-labelledby="cond-h" style="padding: 0; overflow: hidden">
      <h2 id="cond-h" style="padding: 1.25rem 1.25rem 0.75rem">Checks</h2>
      <div style="overflow-x: auto">
        <table data-testid="conditions">
          <thead><tr><th scope="col">Check</th><th scope="col">Status</th><th scope="col">Reason</th><th scope="col">Message</th><th scope="col">Since</th></tr></thead>
          <tbody>
            <tr v-for="c in conditions" :key="c.type">
              <th scope="row">{{ c.type }}</th>
              <td><StatusBadge :badge="conditionBadge(c)" /></td>
              <td class="mono">{{ c.reason }}</td>
              <td style="min-width: 320px">{{ c.message }}<div v-if="c.observedGeneration < env.generation" class="sub">(as of generation {{ c.observedGeneration }})</div></td>
              <td class="nowrap muted" :title="formatTime(c.lastTransitionTime)">{{ relativeTime(c.lastTransitionTime) }}</td>
            </tr>
            <tr v-if="!conditions.length"><td colspan="5" class="muted">No conditions reported yet.</td></tr>
          </tbody>
        </table>
      </div>
    </section>
    </div>

    <!-- ================= data -->
    <section v-else-if="tab === 'data'" class="card" aria-labelledby="data-h">
      <h2 id="data-h">Data</h2>
      <dl class="facts">
        <dt>Data source</dt><dd class="mono">{{ env.source.customer }} / {{ env.source.environment }}<template v-if="sourceSwitch"> (on disk: {{ sourceSwitch }})</template></dd>
        <dt>Classification</dt><dd>{{ env.dataClassification }}</dd>
        <dt>Flavour</dt><dd class="mono">{{ env.db?.flavour ?? env.flavour ?? '—' }}</dd>
        <dt>Backup</dt><dd class="mono">{{ env.db?.backupId ?? '—' }}</dd>
        <dt>Order</dt><dd class="mono">{{ env.db?.order ?? env.spec.order }}</dd>
        <dt>MariaDB</dt><dd>{{ env.db?.mariadbVersion ?? '—' }}</dd>
        <dt>mysql image</dt><dd data-testid="image-mysql"><span class="mono">{{ env.images?.mysql?.image ?? '—' }}</span><div class="sub">{{ imageBasis(env.images?.mysql) }}</div></dd>
        <dt>Base</dt><dd><span class="mono">{{ displayUrl(env.db?.base?.canonicalUrl ?? env.db?.base?.artefactId) }}</span><div v-if="env.db?.base" class="sub mono">sha256 {{ shortSha(env.db.base.sha256) }}</div></dd>
        <dt>Asserts</dt><dd><span class="mono">{{ displayUrl(env.db?.asserts?.canonicalUrl ?? env.db?.asserts?.artefactId) }}</span><div v-if="env.db?.asserts" class="sub mono">sha256 {{ shortSha(env.db.asserts.sha256) }}</div></dd>
        <dt>Dataset generation</dt><dd>{{ env.datasetGeneration }}</dd>
        <dt>Restored</dt><dd>{{ formatTime(env.db?.restoredAt) }}</dd>
        <dt>Prepared</dt><dd>{{ formatTime(env.db?.preparedAt) }}</dd>
        <dt>Asserted</dt><dd>{{ formatTime(env.db?.assertedAt) }}</dd>
        <dt>Keep configuration</dt><dd data-testid="preserve-config">{{ env.spec.preserveConfig ? 'yes: a refresh-data keeps core_config_data' : 'no' }}</dd>
        <template v-if="env.db?.preservedConfig">
          <dt>Kept configuration</dt>
          <dd data-testid="preserved-config">
            <template v-if="env.db.preservedConfig.capturedAt">{{ env.db.preservedConfig.rows }} rows of core_config_data from dataset generation {{ env.db.preservedConfig.fromDatasetGeneration }}, captured {{ formatTime(env.db.preservedConfig.capturedAt) }}</template>
            <template v-else>capturing core_config_data of dataset generation {{ env.db.preservedConfig.fromDatasetGeneration }} before the wipe</template>
            <div v-if="env.db.preservedConfig.sha256" class="sub mono">{{ env.db.preservedConfig.captureId }} · sha256 {{ shortSha(env.db.preservedConfig.sha256) }}</div>
          </dd>
        </template>
      </dl>
      <h3>Patches applied</h3>
      <p v-if="env.db?.patchesApplied === undefined" class="muted">Not observed yet.</p>
      <p v-else-if="!env.db.patchesApplied.length" class="muted">None.</p>
      <ol v-else data-testid="patches" style="display: flex; flex-direction: column; gap: 8px; padding-left: 1.25rem">
        <li v-for="(p, i) in env.db.patchesApplied" :key="i" style="overflow-wrap: anywhere">
          <strong>{{ p.layer ?? 'patch' }}</strong><template v-if="p.customer"> ({{ p.customer }})</template>:
          <span class="mono">{{ displayUrl(p.canonicalUrl ?? p.artefactId) }}</span> <span class="sub mono">sha256 {{ shortSha(p.sha256) }}</span>
        </li>
      </ol>
    </section>

    <!-- ================= application -->
    <section v-else-if="tab === 'application'" class="card" aria-labelledby="app-h">
      <h2 id="app-h">Application</h2>
      <dl class="facts">
        <dt>Peaksolution</dt><dd><strong class="mono">{{ buildLabel(app?.base) }}</strong><div v-if="app?.base?.canonicalUrl" class="sub mono">{{ displayUrl(app.base.canonicalUrl) }}</div><div v-if="app?.base" class="sub" data-testid="app-verification">{{ verificationText(app.base) }}<span v-if="app.base.observed ?? app.base.sha256" class="mono" :title="app.base.observed ?? app.base.sha256"> · sha256 {{ shortSha(app.base.observed ?? app.base.sha256) }}</span></div></dd>
        <dt>Requested build</dt><dd class="mono">{{ buildLabel(env.spec.peaksolution.build.base) }}<span v-if="env.spec.peaksolution.build.base.sha256" class="muted"> · sha256 {{ shortSha(env.spec.peaksolution.build.base.sha256) }}</span><span v-else class="muted"> · no sha256 declared (verified on first fetch)</span></dd>
        <template v-if="app?.addons?.length"><dt>Add-ons</dt><dd><div v-for="(a, i) in app.addons" :key="i" class="mono">{{ buildLabel(a) }} · <span class="muted">{{ verificationText(a) }}</span><span v-if="a.observed ?? a.sha256" :title="a.observed ?? a.sha256"> · {{ shortSha(a.observed ?? a.sha256) }}</span></div></dd></template>
        <dt>Build version</dt><dd class="mono">{{ app?.version ?? '—' }}</dd>
        <dt>Minimum PHP</dt><dd>{{ app?.phpRequirement ?? (app?.fetchedAt ? 'not declared by the build' : '—') }}</dd>
        <dt>php image</dt><dd data-testid="image-php"><span class="mono">{{ env.images?.php?.image ?? '—' }}</span><div class="sub">{{ imageBasis(env.images?.php) }}</div></dd>
        <dt>App generation</dt><dd>{{ app?.generation ?? '—' }}</dd>
        <dt>Fetched</dt><dd>{{ formatTime(app?.fetchedAt) }}</dd>
        <dt>Set up</dt><dd>{{ formatTime(app?.setupAt) }}</dd>
        <dt>Hosts</dt><dd class="mono">{{ env.spec.peaksolution.hosts.join(', ') }}</dd>
        <template v-for="svc in services" :key="svc.key">
          <dt>{{ svc.label }}</dt>
          <dd :data-testid="`service-${svc.key}`">
            <template v-if="svc.requested">
              <strong class="mono">{{ svc.status?.version ?? buildLabel(svc.status ?? svc.requested) }}</strong>
              <span v-if="svc.status?.fetchedAt" class="muted"> · fetched {{ formatTime(svc.status.fetchedAt) }}</span>
              <span v-else class="muted"> · not fetched yet</span>
              <div class="sub mono">{{ displayUrl(svc.requested.url) }}<template v-if="svc.status?.canonicalUrl && svc.status.canonicalUrl !== displayUrl(svc.requested.url)"> (on disk: {{ buildLabel(svc.status) }})</template></div>
              <div v-if="svc.status?.canonicalUrl" class="sub">{{ verificationText(svc.status) }}<span v-if="svc.status.observed ?? svc.status.sha256" class="mono"> · sha256 {{ shortSha(svc.status.observed ?? svc.status.sha256) }}</span></div>
            </template>
            <span v-else class="muted">not enabled: does not run (no build)</span>
            <div v-if="env.spec[svc.key]?.hosts?.length" class="sub mono">{{ env.spec[svc.key]?.hosts?.join(', ') }}</div>
          </dd>
        </template>
        <dt>Chart version</dt><dd class="mono">{{ env.chartVersion ?? '—' }}</dd>
        <dt>Operator version</dt><dd class="mono">{{ env.operatorVersion ?? '—' }}</dd>
        <dt>Generation</dt><dd>{{ env.generation }} (observed {{ env.observedGeneration }})</dd>
        <dt>Paused</dt><dd>{{ env.spec.paused ? 'yes' : 'no' }}</dd>
      </dl>
    </section>

    <!-- ================= activity -->
    <div v-else-if="tab === 'activity'" class="cols">
      <section class="card" aria-labelledby="hist-h" style="flex: 1 1 400px; min-width: 0">
        <div class="card-head">
          <h2 id="hist-h">Action history</h2>
          <label v-if="failedCount" class="inline end" style="font-weight: 400"><input v-model="onlyFailed" type="checkbox" /> Failures only ({{ failedCount }})</label>
        </div>
        <p v-if="env.action.requested" class="muted" style="margin-bottom: 1rem">Last requested: <strong>{{ env.action.requested.type }}</strong> <code>{{ env.action.requested.id }}</code></p>
        <ol class="timeline" data-testid="history">
          <li v-for="h in shownHistory" :key="h.id">
            <div class="rail"><span class="mark" :class="h.outcome === 'Succeeded' ? 'tone-ok' : 'tone-bad'" aria-hidden="true">{{ h.outcome === 'Succeeded' ? '✓' : '✕' }}</span></div>
            <div class="body">
              <div class="inline" style="gap: 4px 10px; align-items: baseline">
                <strong style="font-size: 15px">{{ h.type }}</strong>
                <span :style="`font-weight: 600; font-size: 13px; color: ${h.outcome === 'Succeeded' ? 'var(--ok-fg)' : 'var(--bad-fg)'}`">{{ h.outcome }}</span>
                <span class="muted" style="font-size: 13px" :title="formatTime(h.finishedAt)">{{ formatTime(h.finishedAt) }}</span>
              </div>
              <div class="mono" style="font-size: 12.5px; margin-top: 2px">{{ h.reason }}</div>
              <div class="sub mono">{{ h.id }}</div>
              <details v-if="h.outcome !== 'Succeeded'" style="margin-top: 8px" :open="h === shownHistory.find((x) => x.outcome !== 'Succeeded')">
                <summary style="cursor: pointer; font-size: 13px; color: var(--link)">Details</summary>
                <div class="err-box"><pre class="mono">{{ h.message }}</pre></div>
              </details>
              <div v-else style="margin-top: 4px">{{ h.message }}</div>
            </div>
          </li>
          <li v-if="!shownHistory.length" class="muted" style="display: block">No action has finished yet.</li>
        </ol>
      </section>
      <div style="flex: 1 1 560px; min-width: 0">
        <LiveLog v-if="env.dataAccess" :environment="env.name" :active="active" />
        <p v-else class="alert banner"><Icon name="lock" :size="18" /> <span class="grow">Logs hidden: you are not entitled to the raw data of <code>{{ env.source.customer }}/{{ env.source.environment }}</code>.</span></p>
      </div>
    </div>

    <!-- ================= logs -->
    <template v-else-if="tab === 'logs'">
      <AppLogsPanel v-if="env.dataAccess" :environment="env.name" :raw="env.dataClassification === 'production-raw'"
        :source="`${env.source.customer}/${env.source.environment}`" />
      <p v-else class="alert banner"><Icon name="lock" :size="18" /> <span class="grow">Logs hidden: you are not entitled to the raw data of <code>{{ env.source.customer }}/{{ env.source.environment }}</code>.</span></p>
    </template>

    <!-- ================= database -->
    <template v-else-if="tab === 'database'">
      <DbConsolePanel v-if="env.dataAccess" :environment="env.name" :raw="env.dataClassification === 'production-raw'"
        :source="`${env.source.customer}/${env.source.environment}`" :can-write="canWriteDb" />
      <p v-else class="alert banner"><Icon name="lock" :size="18" /> <span class="grow">Database hidden: you are not entitled to the raw data of <code>{{ env.source.customer }}/{{ env.source.environment }}</code>.</span></p>
    </template>

    <!-- ================= access -->
    <div v-else-if="tab === 'access'" class="cols">
      <div class="main-col">
        <AccessPanel :env="env" @changed="load" />
        <ClassificationPanel :env="env" @changed="load" />
      </div>
      <aside class="side-col">
        <section class="card" aria-labelledby="roles-h">
          <h2 id="roles-h">What each role may do</h2>
          <table class="matrix">
            <thead><tr><th scope="col"><span class="sr-only">Action</span></th><th scope="col">Viewer</th><th scope="col">Operator</th><th scope="col">Owner</th></tr></thead>
            <tbody>
              <tr><th scope="row">See status and logs</th><td>✓</td><td>✓</td><td>✓</td></tr>
              <tr><th scope="row">Stop, start, change build</th><td class="muted">–</td><td>✓</td><td>✓</td></tr>
              <tr><th scope="row">Refresh data</th><td class="muted">–</td><td>✓</td><td>✓</td></tr>
              <tr><th scope="row">Set installation id</th><td class="muted">–</td><td>✓</td><td>✓</td></tr>
              <tr><th scope="row">Accept a data-check violation</th><td class="muted">–</td><td>✓</td><td>✓</td></tr>
              <tr><th scope="row">Tear down, delete</th><td class="muted">–</td><td class="muted">–</td><td>✓</td></tr>
              <tr><th scope="row">Edit the access list</th><td class="muted">–</td><td class="muted">–</td><td>✓</td></tr>
            </tbody>
          </table>
          <p class="hint" style="margin-top: 12px">Everyone on the list sees the environment's status. For raw production data, logs and every action except tear down and delete also need the entitlement to the data. Admins may do everything on every environment and assign the owner.</p>
        </section>
      </aside>
    </div>
  </template>
  <p v-else-if="!error" aria-live="polite">Loading {{ name }}…</p>
</template>
